// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { IconMemo, IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import { StyledClickable } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import get from "@insite/mobius/utilities/get";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import omitMultiple from "@insite/mobius/utilities/omitMultiple";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { color } from "@keller/XDL/Utilities";
import FloatingTooltip, {
    FloatingTooltipProps,
    FloatingTooltipStyles,
} from "@keller/App/Components/FloatingTooltip/FloatingTooltip";
import * as React from "react";
import { FC } from "react";
import styled, { withTheme } from "styled-components";

export interface TooltipPresentationProps {
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
    /** CSS strings or styled-components functions to be injected into nested components. These will override the theme defaults.
     * @themable */
    cssOverrides?: {
        // tooltipClickable?: StyledProp<TooltipProps>;
        // tooltipBody?: StyledProp<TooltipProps>;
        // tooltipContainer?: StyledProp<TooltipProps>;
        // tooltipWrapper?: StyledProp<TooltipProps>;
    };
    /** Props to be passed into the trigger Icon component if no triggerComponent is provided.
     * @themable */
    iconProps?: IconPresentationProps;
    /** Props that will be passed to the typography component rendered in the tooltip.
     * @themable */
    typographyProps?: TypographyPresentationProps;
    /** Extended styles for the underlying FloatingTooltip component.
     * @themable */
    extendedStyles?: FloatingTooltipStyles;
}

export type TooltipComponentProps = MobiusStyledComponentProps<
    "span",
    {
        /** Component to be used as the trigger to optionally replace the default trigger icon. */
        triggerComponent?: React.ReactElement<{ tabIndex: number }>;
        /** The text that will appear in the tooltip */
        text?: string | React.ReactElement | React.ReactNode | React.ReactNode[];
        // XNG-Changes
        /** Function called on tooltip open */
        // onOpen?: React.EventHandler<React.SyntheticEvent>;
        /** Function called on tooltip close */
        // onClose?: React.EventHandler<React.SyntheticEvent>;
        /** Text to be used to describe the tooltip trigger. Used for screenreaders. Defaults to "more info" */
        triggerAltText?: string;
        /** Optionally make the tooltip also display during on mouse hover */
        // displayOnHover?: boolean;
        // XNG-Changes
        trigger?: "hover" | "click" | "manual";
    }
>;

export type TooltipProps = TooltipComponentProps &
    TooltipPresentationProps &
    Omit<FloatingTooltipProps, "triggerComponent">;

const TooltipContainer = styled.div<InjectableCss<any>>`
    &.CMS-Mode {
        border: 0.8px solid #dfe3e6;
        border-radius: 4px;
        background-color: #ffffff;
        box-shadow: 0 2px 4px 0 rgba(109, 120, 120, 0.25);
        padding: 15px;
    }
    ${injectCss}
`;

const TooltipBody = styled.div<InjectableCss<any>>`
    position: relative;
    z-index: 2;
    color: ${color("text.main")};
    ${injectCss}
`;

const TooltipWrapper = styled.span<InjectableCss<any>>`
    ${injectCss}
`;

const TooltipTriggerer = styled.span.attrs({ tabIndex: 0 })<InjectableCss<any>>`
    display: inline-block;
    vertical-align: middle;
    & > ${StyledClickable} {
        display: inline-block;
    }
    &:focus {
        outline: none;
    }

    &&& ${IconWrapper} {
        margin-right: 0;
    }

    ${injectCss}
`;

/**
 * Component that provides supplementatal information on click or enter.
 */

// XNG-Changes - Use FloatingTooltip
const Tooltip: FC<TooltipProps> = props => {
    // XNG-Changes: To fix tooltip style in CMS field tooltip
    const { isEditing: isEditingInShell } = useShellContext();

    const { text, triggerComponent, triggerAltText, mergeCss, trigger = "hover", ...otherProps } = props;
    const { spreadProps, applyStyledProp } = applyPropBuilder(props, { component: "tooltip" });
    const resolvedMergeCss = mergeCss ?? (props as any).theme?.tooltip?.defaultProps?.mergeCss;

    if (
        !text ||
        (typeof text === "string" && text.length === 0) ||
        React.isValidElement(text) ||
        (Array.isArray(text) && text.length === 0)
    ) {
        return triggerComponent || null;
    }

    // XNG-Chnages
    //   handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    //         if (event.keyCode === ESC_KEY) {
    //             event.stopPropagation();
    //             this.closeTooltip(event);
    //         }
    //         if (event.keyCode === ENTER_KEY) {
    //             event.stopPropagation();
    //             this.openTooltip(event);
    //         }
    //     };

    let triggerElement;
    if (triggerComponent) {
        triggerElement = React.cloneElement(triggerComponent, { tabIndex: -1 });
    } else {
        triggerElement = <IconMemo {...spreadProps("iconProps")} />;
    }

    const toolTipComponent = (
        <Typography as="p" {...spreadProps("typographyProps")} data-test-selector="toolTip">
            {text}
        </Typography>
    );

    // let hoverProps;
    // if (displayOnHover) {
    //     hoverProps = {
    //         onMouseEnter: this.openTooltip,
    //         onMouseLeave: this.closeTooltip,
    //     };
    // }

    return (
        <TooltipWrapper
            className="hide-on-print tooltip-container"
            css={applyStyledProp(["cssOverrides", "tooltipWrapper"], resolvedMergeCss)}
        >
            <FloatingTooltip
                trigger={trigger}
                overlay={false}
                triggerComponent={
                    <TooltipTriggerer
                        css={applyStyledProp(["cssOverrides", "tooltipClickable"], resolvedMergeCss)}
                        // XNG-Changes
                        // {...hoverProps}
                    >
                        {triggerElement}
                        <VisuallyHidden>{triggerAltText || props.theme!.translate("more info")}</VisuallyHidden>
                    </TooltipTriggerer>
                }
                offset={({ placement }) => {
                    if (placement === "bottom-start" || placement === "top-start") {
                        return { mainAxis: 25, crossAxis: -20 };
                    }

                    return { mainAxis: 25, crossAxis: 0 };
                }}
                {...otherProps}
                showArrow={true}
                extendedStyles={spreadProps("extendedStyles")}
            >
                <TooltipContainer
                    css={applyStyledProp(["cssOverrides", "tooltipContainer"], resolvedMergeCss)}
                    className={`${isEditingInShell ? "CMS-Mode" : ""}`}
                >
                    <TooltipBody css={applyStyledProp(["cssOverrides", "tooltipBody"], resolvedMergeCss)}>
                        <div aria-live="polite" data-test-selector="tooltip_TooltipInfo" className="tooltipInfo">
                            {toolTipComponent}
                        </div>
                    </TooltipBody>
                </TooltipContainer>
            </FloatingTooltip>
        </TooltipWrapper>
    );
};

/** @component */
export default withTheme(Tooltip);
